import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import icon2x from 'leaflet/dist/images/marker-icon-2x.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Standardna popravka za Leaflet + bundler (webpack/CRA ne resolvuje default marker
// putanje same od sebe) — koristi slike iz samog leaflet paketa, bez CDN zavisnosti.
L.Icon.Default.mergeOptions({
    iconUrl: icon,
    iconRetinaUrl: icon2x,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});

// Beograd — razuman default centar kad geolokacija nije dostupna/odbijena.
const DEFAULT_CENTER = [44.7866, 20.4489];

// Klik bilo gde na mapu pomera marker na tu tačku.
const ClickHandler = ({ onPick }) => {
    useMapEvents({ click: (e) => onPick([e.latlng.lat, e.latlng.lng]) });
    return null;
};

const LocationPickerModal = ({ onConfirm, onClose }) => {
    const { t } = useTranslation();
    const [position, setPosition] = useState(null);
    const [center, setCenter] = useState(DEFAULT_CENTER);
    const [locating, setLocating] = useState(true);
    const mapRef = useRef(null);

    // Best-effort: centriraj na trenutnu lokaciju ako je dozvoljena, inače ostani na default centru.
    useEffect(() => {
        if (!navigator.geolocation) { setLocating(false); return; }
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                const c = [coords.latitude, coords.longitude];
                setCenter(c);
                setPosition(c);
                setLocating(false);
            },
            () => setLocating(false),
            { timeout: 8000 }
        );
    }, []);

    const goToMyLocation = () => {
        if (!navigator.geolocation) {
            window.alert(t('chat.lokacijaNepodrzano'));
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                const c = [coords.latitude, coords.longitude];
                setPosition(c);
                mapRef.current?.flyTo(c, 15);
                setLocating(false);
            },
            (err) => {
                setLocating(false);
                const poruka = err.code === 1 ? t('chat.lokacijaOdbijena')
                             : err.code === 3 ? t('chat.lokacijaTimeout')
                             : t('chat.lokacijaGreska');
                window.alert(poruka);
            },
            { timeout: 8000 }
        );
    };

    return (
        <div className="chat-modal-overlay" onClick={onClose}>
            <div className="chat-modal chat-location-modal" onClick={e => e.stopPropagation()}>
                <div className="chat-modal-header">
                    <h3>{t('chat.pickLocation')}</h3>
                    <button className="chat-modal-close" onClick={onClose}>
                        <i className="pi pi-times" />
                    </button>
                </div>
                <div className="chat-modal-body chat-location-body">
                    <p className="chat-location-hint">{t('chat.pickLocationHint')}</p>
                    <div className="chat-location-map-wrap">
                        <MapContainer attributionControl={false}
                            center={center}
                            zoom={13}
                            style={{ height: '100%', width: '100%' }}
                            ref={mapRef}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <ClickHandler onPick={setPosition} />
                            {position && <Marker position={position} />}
                        </MapContainer>
                        <button type="button" className="chat-location-my-btn" onClick={goToMyLocation} disabled={locating}>
                            <i className="pi pi-map-marker" /> {t('chat.myLocation')}
                        </button>
                    </div>
                    {position && (
                        <div className="chat-location-coords">
                            {position[0].toFixed(5)}, {position[1].toFixed(5)}
                        </div>
                    )}
                </div>
                <div className="chat-modal-footer">
                    <button className="chat-modal-btn-cancel" onClick={onClose}>{t('common.cancel')}</button>
                    <button
                        className="chat-modal-btn-create"
                        disabled={!position}
                        onClick={() => position && onConfirm(position[0], position[1])}
                    >
                        {t('chat.sendLocationBtn')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LocationPickerModal;
